import { useEffect, useRef, type ReactNode } from "react";

import type { MailFolder } from "../lib/types.js";
import { IconFolderPlus, IconPencil, IconTrash } from "./icons.js";

/**
 * Het menu achter de kebab op een maprij.
 *
 * Volgt de conventies van AccountMenu — het enige andere popup-menu in de app:
 * role="menu", sluiten bij een pointerdown erbuiten, Escape terug naar de knop,
 * pijltjes tussen de items en meteen het eerste item focussen.
 */

interface FolderMenuProps {
  folder: MailFolder;
  /** De kebab waar het menu onder hangt; de focus gaat daar naartoe terug. */
  anchor: HTMLElement;
  onCreateChild: () => void;
  onRename: () => void;
  onDelete: () => void;
  onClose: () => void;
}

export function FolderMenu({
  folder,
  anchor,
  onCreateChild,
  onRename,
  onDelete,
  onClose,
}: FolderMenuProps): ReactNode {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        anchor.focus();
        return;
      }
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      e.preventDefault();
      const items = menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]');
      if (items === undefined || items.length === 0) return;
      const list = [...items];
      const at = list.indexOf(document.activeElement as HTMLButtonElement);
      const next =
        e.key === "ArrowDown"
          ? list[(at + 1) % list.length]
          : list[(at - 1 + list.length) % list.length];
      next?.focus();
    }

    function onPointerDown(e: PointerEvent): void {
      if (menuRef.current?.contains(e.target as Node) === true) return;
      onClose();
    }

    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [anchor, onClose]);

  // Meteen het eerste item focussen, zodat pijltjes en Enter direct werken.
  useEffect(() => {
    menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
  }, []);

  // Het menu hangt aan de linkerrand van de zijbalk uitgelijnd onder de kebab.
  const rect = anchor.getBoundingClientRect();

  return (
    <div
      ref={menuRef}
      className="folder-menu"
      role="menu"
      aria-label={`Acties voor ${folder.name}`}
      style={{ top: `${rect.bottom + 4}px`, left: `${Math.max(8, rect.right - 200)}px` }}
    >
      <button type="button" role="menuitem" className="account-menu-item" onClick={onCreateChild}>
        <IconFolderPlus />
        Nieuwe submap…
      </button>
      <button type="button" role="menuitem" className="account-menu-item" onClick={onRename}>
        <IconPencil />
        Naam wijzigen…
      </button>
      <div className="account-menu-sep" role="separator" />
      <button
        type="button"
        role="menuitem"
        className="account-menu-item account-menu-item--danger"
        onClick={onDelete}
      >
        <IconTrash />
        Map verwijderen…
      </button>
    </div>
  );
}
